import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useMercenary } from '../../../contexts/MercenaryContext';
import Toast from '../../Shared/Toast';
import { loadCookiePrefs } from '../TeamBattle/lobbyPreferences';
import styles from './CreateRankedLobby.module.css';

const RANKED_MIN_LEVEL = 10;

const getLobbyPrefsKey = (mercId: number) => `ranked_lobby_prefs_${mercId}`;

interface LobbyPrefs {
  minLevelOffset: number;
  maxLevelOffset: number;
  maxPlayers: number;
  randomTeams: boolean;
  name?: string;
}

const CreateRankedLobby: React.FC = () => {
  const { mercenary } = useMercenary();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [minLevel, setMinLevel] = useState(RANKED_MIN_LEVEL);
  const [maxLevel, setMaxLevel] = useState(RANKED_MIN_LEVEL + 4);
  const [maxPlayers, setMaxPlayers] = useState(4);
  const [randomTeams, setRandomTeams] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const applyDefaults = (merc: NonNullable<typeof mercenary>) => {
    setName((merc.name.replace(/[^a-zA-Z0-9 ]/g, '').trim() + ' Ranked Battle').slice(0, 30));
    setMinLevel(Math.max(RANKED_MIN_LEVEL, merc.level - 2));
    setMaxLevel(merc.level + 4);
  };

  useEffect(() => {
    if (!mercenary) return;
    const saved = localStorage.getItem(getLobbyPrefsKey(mercenary.id));
    if (saved) {
      try {
        const prefs: LobbyPrefs = JSON.parse(saved);
        setName((prefs.name ? prefs.name.replace(/[^a-zA-Z0-9 ]/g, '').trim() : '') || (mercenary.name.replace(/[^a-zA-Z0-9 ]/g, '').trim() + ' Ranked Battle').slice(0, 30));
        setMinLevel(Math.max(RANKED_MIN_LEVEL, Math.max(mercenary.level - 2, mercenary.level + prefs.minLevelOffset)));
        setMaxLevel(Math.max(mercenary.level, Math.min(mercenary.level + 4, mercenary.level + prefs.maxLevelOffset)));
        setMaxPlayers(prefs.maxPlayers);
        setRandomTeams(prefs.randomTeams);
      } catch {
        applyDefaults(mercenary);
      }
    } else {
      applyDefaults(mercenary);
    }
  }, [mercenary]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mercenary?.id) return;
    setIsLoading(true);
    const lobbyName = name.trim() || (mercenary.name.replace(/[^a-zA-Z0-9 ]/g, '').trim() + ' Ranked Battle').slice(0, 30);
    try {
      const cookiePrefs = loadCookiePrefs(mercenary.id, 'normal');
      const savedPreferences = {
        surrenderHP: cookiePrefs?.surrenderHP ?? 0.5,
        readyHP: cookiePrefs?.readyHP ?? 0.9,
        strategy: cookiePrefs?.strategy ?? 'Normal',
      };
      const response = await axios.post(
        '/api/team-battle/create',
        {
          name: lobbyName,
          minLevel,
          maxLevel,
          maxPlayers,
          randomTeams,
          matchType: 'normal',
          pve: false,
          isRanked: true,
          mercId: mercenary.id,
        },
        { withCredentials: true }
      );
      const lobbyId = response.data.lobby.id;
      await axios.put(
        `/api/team-battle/lobbies/${lobbyId}/participant/${mercenary.id}`,
        savedPreferences,
        { withCredentials: true }
      );
      localStorage.setItem(getLobbyPrefsKey(mercenary.id), JSON.stringify({
        minLevelOffset: minLevel - mercenary.level,
        maxLevelOffset: maxLevel - mercenary.level,
        maxPlayers,
        randomTeams,
        name: lobbyName,
      } as LobbyPrefs));
      navigate(`/team-battle/lobby/${lobbyId}`);
    } catch (err: any) {
      setToastType('error');
      setToastMessage(err?.response?.data?.message ?? 'Failed to create ranked lobby.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!mercenary) return null;

  const meetsMinLevel = mercenary.level >= RANKED_MIN_LEVEL;
  const minLevelMin = Math.max(RANKED_MIN_LEVEL, mercenary.level - 2);
  const minLevelMax = mercenary.level;
  const maxLevelMin = mercenary.level;
  const maxLevelMax = mercenary.level + 4;

  const bracketLabel: Record<number, string> = { 4: '2v2', 6: '3v3', 8: '4v4' };

  return (
    <div className={styles.createLobbyContainer}>
      {toastMessage && (
        <Toast message={toastMessage} type={toastType} duration={3000} onClose={() => setToastMessage(null)} />
      )}
      <h1 className={styles.title}>⚔ Create Ranked Lobby</h1>
      <div className={styles.titleDecor}>- Competitive Arena -</div>
      <hr className={styles.divider} />

      {!meetsMinLevel && (
        <div className={styles.warningBanner}>
          Ranked battles require level {RANKED_MIN_LEVEL}. Your mercenary is level {mercenary.level}.
        </div>
      )}

      <form className={styles.form} onSubmit={handleCreate}>

        <div className={styles.formGroup}>
          <label>Lobby Name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value.replace(/[^a-zA-Z0-9 ]/g, '').slice(0, 30))}
            placeholder={(mercenary.name.replace(/[^a-zA-Z0-9 ]/g, '').trim() + ' Ranked Battle').slice(0, 30)}
            maxLength={30}
          />
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>Min Level</label>
            <div className={styles.numberInputWrapper}>
              <button type="button" className={styles.numberBtn}
                onClick={() => setMinLevel(l => Math.max(minLevelMin, l - 1))}>−</button>
              <input type="number" value={minLevel} readOnly className={styles.numberControl} />
              <button type="button" className={styles.numberBtn}
                onClick={() => setMinLevel(l => Math.min(minLevelMax, l + 1))}>+</button>
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Max Level</label>
            <div className={styles.numberInputWrapper}>
              <button type="button" className={styles.numberBtn}
                onClick={() => setMaxLevel(l => Math.max(maxLevelMin, l - 1))}>−</button>
              <input type="number" value={maxLevel} readOnly className={styles.numberControl} />
              <button type="button" className={styles.numberBtn}
                onClick={() => setMaxLevel(l => Math.min(maxLevelMax, l + 1))}>+</button>
            </div>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label>Bracket</label>
          <select value={maxPlayers} onChange={e => setMaxPlayers(+e.target.value)}>
            <option value={4}>2 vs. 2</option>
            <option value={6}>3 vs. 3</option>
            <option value={8}>4 vs. 4</option>
          </select>
          <span className={styles.bracketHint}>Bracket: {bracketLabel[maxPlayers] || '?'}</span>
        </div>

        <div className={styles.toggleRow}>
          <div className={styles.toggleLabel}>
            <span className={styles.toggleLabelMain}>Random Teams</span>
            <span className={styles.toggleLabelSub}>Auto-assign mercenaries when lobby fills</span>
          </div>
          <label className={styles.toggle}>
            <input type="checkbox" checked={randomTeams} onChange={e => setRandomTeams(e.target.checked)} />
            <span className={styles.toggleSlider}></span>
          </label>
        </div>

        <div className={styles.infoBox}>
          <strong>Ranked Rules:</strong>
          <ul>
            <li>PvP only, no beasts, no death matches</li>
            <li>Glicko rating adjusted after each match</li>
            <li>25 ranked battles per day</li>
          </ul>
        </div>

        <div className={styles.formGroup}>
          <button type="submit" className={styles.createLobbyButton} disabled={isLoading || !meetsMinLevel}>
            {isLoading ? 'Creating…' : '⚔ Create Ranked Lobby'}
          </button>
          <button type="button" className={styles.backButton} onClick={() => navigate('/ranked-battle')}>
            ← Back to Ranked Lobbies
          </button>
        </div>

      </form>
    </div>
  );
};

export default CreateRankedLobby;
